/**
 * InvoiceViewHeader
 *
 * Renders the top action bar: document title, status badge, and action buttons.
 */

import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from '@elsapiens/ui'
import { Banknote, History, Download, Pencil, Copy, Ban, ChevronDown } from 'lucide-react'

import { STATUS_COLORS, STATUS_LABELS } from './invoice-view-helpers'
import { Button } from '../../ui'

import type { InvoiceViewProps } from '../InvoiceView'

interface InvoiceViewHeaderProps
  extends Pick<
    InvoiceViewProps,
    | 'invoice'
    | 'onGeneratePDF'
    | 'onViewHistory'
    | 'onRecordPayment'
    | 'onEdit'
    | 'onClone'
    | 'onPost'
    | 'onVoid'
    | 'isLoading'
  > {
  docLabel: string
}

function InvoiceViewHeaderTitle({ invoice, docLabel }: Pick<InvoiceViewHeaderProps, 'invoice' | 'docLabel'>): JSX.Element {
  const statusStyle = STATUS_COLORS[invoice.status] ?? STATUS_COLORS.draft
  return (
    <div className="flex items-center el-gap-field min-w-0">
      <h1 className="text-lg font-semibold text-foreground">
        {docLabel} {invoice.invoiceNumber}
      </h1>
      <span
        className="status-badge"
        style={{ backgroundColor: statusStyle.bg, color: statusStyle.text }}
      >
        {STATUS_LABELS[invoice.status] ?? invoice.status}
      </span>
    </div>
  )
}

export function InvoiceViewHeader({
  invoice,
  docLabel,
  onGeneratePDF,
  onViewHistory,
  onRecordPayment,
  onEdit,
  onClone,
  onPost,
  onVoid,
  isLoading = false,
}: InvoiceViewHeaderProps): JSX.Element {
  const canEdit =
    invoice.status === 'draft' ||
    (invoice.status === 'posted' && !invoice.irn && !invoice.ewayBillNumber)

  return (
    <div
      className="flex-shrink-0 border-b border-border bg-card el-px-lg el-py-sm"
      style={{ borderLeft: '1px solid transparent', paddingLeft: 'calc(var(--el-layout-gap-lg) + 1px)' }}
    >
      <div className="el-px-lg">
        <div className="flex items-center justify-between el-gap-md">
          <InvoiceViewHeaderTitle docLabel={docLabel} invoice={invoice} />
          <InvoiceViewHeaderActions
            canEdit={canEdit}
            docLabel={docLabel}
            invoice={invoice}
            isLoading={isLoading}
            onClone={onClone}
            onEdit={onEdit}
            onGeneratePDF={onGeneratePDF}
            onPost={onPost}
            onRecordPayment={onRecordPayment}
            onViewHistory={onViewHistory}
            onVoid={onVoid}
          />
        </div>
      </div>
    </div>
  )
}

interface ActionsProps extends Omit<InvoiceViewHeaderProps, 'docLabel'> {
  docLabel: string
  canEdit: boolean
}

function PaymentActionButton({ status, isLoading, onRecordPayment }: { status: string; isLoading: boolean; onRecordPayment?: () => void }): JSX.Element | null {
  if (!onRecordPayment) { return null }
  if (status !== 'posted' && status !== 'draft') { return null }
  return (
    <Button disabled={isLoading} size="sm" variant="secondary" onClick={onRecordPayment}>
      <Banknote className="w-3.5 h-3.5 mr-1" />
      {status === 'posted' ? 'Record Payment' : 'Record Advance'}
    </Button>
  )
}

// Secondary actions live in an overflow ("Actions") menu so the bar stays
// uncluttered; only the primary CTAs (Record Payment, Post) remain as buttons.
function InvoiceActionsMenu(p: ActionsProps): JSX.Element {
  const isPosted = p.invoice.status === 'posted'
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className="inline-flex items-center gap-1 rounded-md border border-border bg-card el-px-md h-8 text-sm font-medium text-foreground hover:bg-background-secondary transition-colors disabled:opacity-50"
        disabled={p.isLoading}
      >
        Actions
        <ChevronDown className="w-3.5 h-3.5" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={p.onGeneratePDF}>
          <Download className="w-3.5 h-3.5 mr-2" />Download PDF
        </DropdownMenuItem>
        {p.onViewHistory && (
          <DropdownMenuItem onClick={p.onViewHistory}>
            <History className="w-3.5 h-3.5 mr-2" />History
          </DropdownMenuItem>
        )}
        {p.canEdit && p.onEdit && (
          <DropdownMenuItem onClick={p.onEdit}>
            <Pencil className="w-3.5 h-3.5 mr-2" />Edit
          </DropdownMenuItem>
        )}
        {p.onClone && (
          <DropdownMenuItem onClick={p.onClone}>
            <Copy className="w-3.5 h-3.5 mr-2" />Clone {p.docLabel}
          </DropdownMenuItem>
        )}
        {isPosted && p.onVoid && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="text-red-600 dark:text-red-400" onClick={p.onVoid}>
              <Ban className="w-3.5 h-3.5 mr-2" />Void {p.docLabel}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function InvoiceViewHeaderActions(p: ActionsProps): JSX.Element {
  return (
    <div className="flex items-center gap-2 flex-shrink-0">
      <PaymentActionButton isLoading={p.isLoading ?? false} status={p.invoice.status} onRecordPayment={p.onRecordPayment} />
      {p.invoice.status === 'draft' && p.onPost && (
        <Button disabled={p.isLoading} size="sm" variant="primary" onClick={p.onPost}>Post {p.docLabel}</Button>
      )}
      <InvoiceActionsMenu {...p} />
    </div>
  )
}
